import React, { useCallback, useMemo, useState, useEffect, useRef, useLayoutEffect } from 'react';
import { PlusCircle } from 'lucide-react';
import SectionHeader from '../../components/shared/ui/SectionHeader';
import SectionContainer from '../../components/shared/ui/SectionContainer';
import CreatorCard from './CreatorCard';
import { CreatorsSkeleton } from '../../components/shared/ui/LazyFallbacks';
import { useUser } from '../../contexts/UserContext';
import { Creator } from '../../types';
import CreatorModal from '../../components/shared/ui/CreatorModal';
import ImageCropModal from '../../components/shared/common/ImageCropModal';
import { AnimatePresence, motion } from 'framer-motion';
import { useSimpleSnapshot } from '../../hooks/useSimpleSnapshot';
import { useSnapshotScope } from '../../contexts/SnapshotScope';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { logger } from '../../utils/logger';
import { getCurrentWeekKey, getWeekStartDateFromKey } from '../../utils/weekKeyUtils';
import { ALLOW_PAST_WEEK_EDITS } from '../../config/auth';


const MAX_CREATORS = 12;


// Memoized creator card component to prevent unnecessary re-renders
const MemoizedCreatorCard = React.memo<{
  creator: Creator;
  onUpdate: (creator: Creator) => void;
  onDelete: () => void;
  role: 'admin' | 'viewer';
  onCropImageClick: () => void;
  onUploadImage: (file: File, id: string) => void;
  onImageDelete: (id: string) => void;
  imagePriority?: boolean;
}>(({ creator, onUpdate, onDelete, role, onCropImageClick, onUploadImage, onImageDelete, imagePriority }) => (
  <CreatorCard
    creator={creator}
    onUpdate={onUpdate}
    onDelete={() => onDelete()}
    role={role}
    onCropImageClick={onCropImageClick}
    onUploadImage={onUploadImage}
    onImageDelete={onImageDelete}
    imagePriority={imagePriority}
  />
));

MemoizedCreatorCard.displayName = 'MemoizedCreatorCard';

// CreatorCard with scroll animation wrapper (Phase 3 - staggered)
const CreatorCardWithAnimation: React.FC<{
  creator: Creator;
  index: number;
  staggerDelay: number;
  onUpdate: (creator: Creator) => void;
  onDelete: () => void;
  role: 'admin' | 'viewer';
  onCropImageClick: () => void;
  onUploadImage: (file: File, id: string) => void;
  onImageDelete: (id: string) => void;
  imagePriority?: boolean;
}> = React.memo(({ creator, staggerDelay, onUpdate, onDelete, role, onCropImageClick, onUploadImage, onImageDelete, imagePriority }) => {
  const scrollAnim = useScrollAnimation({
    threshold: 0.1,
    rootMargin: '50px',
    triggerOnce: true,
    staggerDelay
  });

  return (
    <motion.div
      ref={scrollAnim.ref}
      style={scrollAnim.style}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ 
        duration: 0.4,
        ease: [0.4, 0, 0.2, 1]
      }}
    >
      <MemoizedCreatorCard
        creator={creator}
        onUpdate={onUpdate}
        onDelete={onDelete}
        role={role}
        onCropImageClick={onCropImageClick}
        onUploadImage={onUploadImage}
        onImageDelete={onImageDelete}
        imagePriority={imagePriority}
      />
    </motion.div>
  );
});

CreatorCardWithAnimation.displayName = 'CreatorCardWithAnimation';

interface RecommendedCreatorsProps {
  className?: string;
  bootData?: Creator[]; // Optional boot data for instant render
}

const RecommendedCreators: React.FC<RecommendedCreatorsProps> = React.memo(({
  className = '',
  bootData: initialBootData
}) => {
  const { canEditSection, isActuallyAdmin } = useUser();
  const { workspaceId, periodKey } = useSnapshotScope();
  const isAdmin = isActuallyAdmin; // Check actual role, not effective role (viewer mode)
  
  // Mobile optimization: Device detection
  const deviceInfo = useMemo(() => {
    const userAgent = navigator.userAgent;
    const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
    const hardwareConcurrency = navigator.hardwareConcurrency || 4;
    const deviceMemory = navigator.deviceMemory || 4;
    const isLowEnd = isMobile && (hardwareConcurrency <= 2 || deviceMemory <= 2);
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    return { isMobile, isLowEnd, prefersReducedMotion };
  }, []);
  
  // Check if current week, future week, or past week editing is allowed
  const currentWeekKey = getCurrentWeekKey();
  const currentWeekDate = getWeekStartDateFromKey(currentWeekKey);
  const periodDate = getWeekStartDateFromKey(periodKey);
  const isCurrentWeek = periodKey === currentWeekKey;
  const isFutureWeek = currentWeekDate && periodDate && periodDate.getTime() > currentWeekDate.getTime();
  const isPastWeek = currentWeekDate && periodDate && periodDate.getTime() < currentWeekDate.getTime();
  
  // Map contextual role to component's expected union type
  const cardRole: 'admin' | 'viewer' = canEditSection('recommendedCreators') ? 'admin' : 'viewer';
  
  // Admins can always edit past weeks (for cumulative dashboard, mistake corrections, etc.)
  // Viewers can only edit current/future weeks (or past weeks if explicitly enabled)
  const isEditable = canEditSection('recommendedCreators') && (isCurrentWeek || isFutureWeek || (isPastWeek && (isAdmin || ALLOW_PAST_WEEK_EDITS)));
  
  // Debug: Log editability status (remove in production if not needed)
  useEffect(() => {
    if (import.meta.env.DEV) {
      logger.debug('RecommendedCreators editability', {
        canEditSection: canEditSection('recommendedCreators'),
        isCurrentWeek,
        isFutureWeek,
        isPastWeek,
        ALLOW_PAST_WEEK_EDITS,
        isEditable,
        periodKey,
        currentWeekKey
      }, 'RecommendedCreators');
    }
  }, [canEditSection, isCurrentWeek, isFutureWeek, isPastWeek, isEditable, periodKey]);

  // ✅ CRITICAL FIX: Always enable hook for save functionality
  // Boot data is just for initial render, but hook must be enabled for saves to work
  // If hook is disabled, saves won't work and data will disappear
  const defaults: Creator[] = useMemo(() => [], []);
  const { 
    data: fetchedCreators, 
    save, 
    rootKey: sectionRootKey
  } = useSimpleSnapshot<Creator[]>({
    workspaceId,
    section: 'recommendedCreators',
    periodKey,
    defaultValue: defaults,
    enabled: true // ✅ Always enabled - boot data is just for initial render
  });
  
  // Track if we've already synced boot data to prevent restoring deleted items
  const hasSyncedBootDataRef = useRef(false);
  // Track if hook data has loaded (even if empty) to distinguish from "not loaded yet"
  const hasLoadedRef = useRef(false);
  
  // Track when fetched data loads (even if empty)
  useEffect(() => {
    if (fetchedCreators !== undefined) {
      hasLoadedRef.current = true;
    }
  }, [fetchedCreators]);
  
  // Sync boot data to hook state when provided (for instant render)
  // CRITICAL: Only sync once on initial mount when boot data exists and fetched data hasn't loaded yet
  // This prevents restoring deleted items when array becomes empty
  useEffect(() => {
    if (initialBootData && Array.isArray(initialBootData) && initialBootData.length > 0) {
      // Only sync if we haven't synced yet AND hook data hasn't loaded yet
      // This prevents restoring deleted items when array becomes empty
      if (!hasSyncedBootDataRef.current && !hasLoadedRef.current) {
        hasSyncedBootDataRef.current = true;
        save(initialBootData).catch((error) => {
          if (import.meta.env.DEV) {
            logger.error('Failed to sync boot data', { error }, 'RecommendedCreators');
          }
        });
      }
    }
  }, [initialBootData, save]);
  
  // Use boot data if available (instant), otherwise use fetched data
  // CRITICAL: Always use fetchedCreators for saves to ensure data persistence
  // Type guard: ensure creators is always an array
  const creators: Creator[] = (() => {
    const data = (typeof fetchedCreators !== 'undefined') ? (fetchedCreators || []) : (initialBootData || []);
    return Array.isArray(data) ? data : [];
  })();

  // Last rendered list height (don't reset on week change)
  const rcListHostRef = useRef<HTMLDivElement>(null);
  const rcLastHRef = useRef<number>(320); // sensible baseline so it never collapses

  useLayoutEffect(() => {
    if (rcListHostRef.current) {
      const h = rcListHostRef.current.offsetHeight;
      if (h > 0) rcLastHRef.current = h;
    }
  }, [creators.length]); // runs right after DOM paints real list

  
  // Auto-save is handled by useSimpleSnapshot hook
  
  // Check if selected week is editable (within 14 days)
  
  // canEditSection is already properly implemented to check effective role (viewerMode)
  


  // Note: Auto-save is handled automatically by useSimpleSnapshot hook

  // Memoized creator management handlers
  const handleAddCreator = useCallback(() => {
    if (!canEditSection('recommendedCreators')) {
      return;
    }
    
    if (creators.length >= MAX_CREATORS) {
      return;
    }
    
    const newCreator: Creator = { 
      id: crypto.randomUUID(), 
      name: '', 
      profileImage: null, 
      links: [],
      profileUrls: []
    };
    
    try {
      const newCreatorsArray = [...creators, newCreator];
      save(newCreatorsArray);
    } catch (error) {
      if (import.meta.env.DEV) {
        logger.error('Failed to add creator', { error }, 'RecommendedCreators');
      }
    }
  }, [creators, save, canEditSection]);

  const handleUpdateCreator = useCallback((updatedCreator: Creator) => {
    // Type guard: ensure updatedCreator has required fields
    if (!updatedCreator || !updatedCreator.id) {
      if (import.meta.env.DEV) {
        logger.warn('Invalid creator update', { creator: updatedCreator }, 'RecommendedCreators');
      }
      return;
    }
    
    // Allow viewers to update approval status, but block other edits
    const isApprovalUpdate = updatedCreator.viewerApproval && 
      (updatedCreator.viewerApproval === 'accepted' || updatedCreator.viewerApproval === 'declined');
    
    // Allow approval updates even in past weeks (viewer functionality)
    if (!isEditable && !isApprovalUpdate) {
      return; // Prevent updating if week is locked (except for approval updates)
    }
    
    const updated = creators.map(c => c.id === updatedCreator.id ? updatedCreator : c);
    save(updated);
  }, [isEditable, creators, save]);

  const handleDeleteCreator = useCallback((creatorId: string) => {
    if (!isEditable) {
      return; // Prevent deleting if week is locked
    }
    
    if (!creatorId) {
      if (import.meta.env.DEV) {
        logger.warn('Invalid creator ID for deletion', { creatorId }, 'RecommendedCreators');
      }
      return;
    }
    
    const updated = creators.filter(c => c.id !== creatorId);
    save(updated);
  }, [isEditable, creators, save]);


  // UI toggles for sorting/filtering
  const [showAll, setShowAll] = useState(false);
  const [sortMode, setSortMode] = useState<'recent' | 'roi'>('roi');

  // Sort creators by selected mode
  const sortedCreators = useMemo(() => {
    if (creators.length === 0) return [];
    const arr = [...creators];
    if (sortMode === 'recent') {
      // Most recently added first (by ID timestamp if available, otherwise preserve order)
      arr.sort((a, b) => {
        // Try to extract timestamp from ID if it's a UUID with timestamp
        const aTime = a.id.includes('_') ? parseInt(a.id.split('_')[1] || '0', 10) : 0;
        const bTime = b.id.includes('_') ? parseInt(b.id.split('_')[1] || '0', 10) : 0;
        return bTime - aTime;
      });
    } else {
      // High ROI (rates) first - handle undefined/null rates
      arr.sort((a, b) => {
        const aRate = typeof a.rates === 'number' ? a.rates : 0;
        const bRate = typeof b.rates === 'number' ? b.rates : 0;
        return bRate - aRate;
      });
    }
    return arr;
  }, [creators, sortMode]);

  // Show all creators when editing, or top 3 when viewing
  const visibleCreators = (isEditable || showAll) ? sortedCreators : sortedCreators.slice(0, 3);

  const [modalOpen, setModalOpen] = useState(false);


  const [selectedCreatorId, setSelectedCreatorId] = useState<string | null>(null);
  const [imageToCrop, setImageToCrop] = useState<string | null>(null);
  const [showCropModal, setShowCropModal] = useState(false);

  const handleEditImage = (creator: Creator) => {
    setSelectedCreatorId(creator.id);
    setImageToCrop(creator.profileImage || null);
    setShowCropModal(true);
  };

  const handleCropComplete = useCallback((creatorId: string, croppedImage: string) => {
    if (!isEditable || !creatorId) return;
    const updated = creators.map(c => 
      c.id === creatorId 
        ? { ...c, profileImage: croppedImage, image: croppedImage } 
        : c
    );
    save(updated);
    setShowCropModal(false);
    setImageToCrop(null);
    setSelectedCreatorId(null);
  }, [creators, save, isEditable]);

  const handleImageUpload = (file: File, creatorId: string) => {
    const reader = new FileReader();
    reader.onload = () => {
      setImageToCrop(reader.result as string);
      setSelectedCreatorId(creatorId);
      setShowCropModal(true);
    };
    reader.readAsDataURL(file);
  };

  const handleImageDelete = useCallback((creatorId: string) => {
    if (!isEditable || !creatorId) return;
    const updated = creators.map(c => 
      c.id === creatorId 
        ? { ...c, profileImage: null, image: null } 
        : c
    );
    save(updated);
    // Clear crop modal state if it was open for this creator
    if (selectedCreatorId === creatorId) {
      setShowCropModal(false);
      setImageToCrop(null);
      setSelectedCreatorId(null);
    }
  }, [creators, save, isEditable, selectedCreatorId]);

  // Memoize the grid items with scroll animations (Phase 3 - staggered)
  const gridItems = useMemo(() =>
    (visibleCreators || []).map((creator, index) => {
      // Stagger delay for smooth reveal (Phase 3)
      const staggerDelay = deviceInfo.prefersReducedMotion ? 0 : Math.min(index * 50, 500);
      // Prioritize first 2 images for faster initial render
      const imagePriority = index < 2;
      
      return (
        <CreatorCardWithAnimation
          key={creator.id}
          creator={creator}
          index={index}
          staggerDelay={staggerDelay}
          onUpdate={handleUpdateCreator}
          onDelete={canEditSection('recommendedCreators') ? () => handleDeleteCreator(creator.id) : () => {}}
          role={cardRole}
          onCropImageClick={canEditSection('recommendedCreators') ? () => handleEditImage(creator) : () => {}}
          onUploadImage={canEditSection('recommendedCreators') ? (file) => handleImageUpload(file, creator.id) : () => {}}
          onImageDelete={canEditSection('recommendedCreators') ? (id) => handleImageDelete(id) : () => {}}
          imagePriority={imagePriority}
        />
      );
    }),
    [visibleCreators, handleUpdateCreator, handleDeleteCreator, canEditSection, handleImageUpload, handleImageDelete, handleEditImage]
  );

  // Dev-only skeleton guard
  if (typeof fetchedCreators === 'undefined' && !initialBootData && import.meta.env.DEV) {
    return (
      <SectionContainer 
        className={className} 
        variant="bare"
        testId="section-recommended-creators"
        aria-label="Recommended Creators section"
      >
        <SectionHeader 
          title="Recommended Creators" 
          href="https://docs.google.com/creators-master"
        />
        <div className="mt-6">
          <CreatorsSkeleton />
        </div>
      </SectionContainer>
    );
  }

  return (
    <SectionContainer 
      data-key={sectionRootKey} 
      data-section="recommended-creators" 
      className={className} 
      variant="bare"
      testId="section-recommended-creators"
      aria-label="Recommended Creators section"
    >
      <SectionHeader 
        title="Recommended Creators" 
        href="https://docs.google.com/creators-master"
      >
        {isEditable && (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleAddCreator();
            }}
            disabled={creators.length >= MAX_CREATORS}
            className="inline-flex items-center gap-2 px-4 py-2 bg-accent text-white rounded-lg hover:bg-accentStrong disabled:bg-disabledBg disabled:text-disabledText disabled:cursor-not-allowed transition-colors"
            title={creators.length >= MAX_CREATORS ? 'Maximum creators reached' : 'Add new creator'}
          >
            <PlusCircle size={16} />
            Add Creator
          </button>
        )}
      </SectionHeader>
      {/* Toggles for sorting/filtering */}
      <div className="flex items-center gap-4 mt-6 mb-6 z-10">
        <div className="flex gap-2">
          <button
            className={`updates-toggle ${sortMode === 'roi' ? 'updates-toggle--active' : ''}`}
            onClick={() => setSortMode('roi')}
          >
            Highest Rate
          </button>
          <button
            className={`updates-toggle ${sortMode === 'recent' ? 'updates-toggle--active' : ''}`}
            onClick={() => setSortMode('recent')}
          >
            Recently Added
          </button>
        </div>
        <button
          className="ml-auto updates-toggle"
          onClick={() => setShowAll(v => !v)}
        >
          {showAll ? 'Show Top 3' : 'Show All'}
        </button>
      </div>
      
      {/* Creators grid */}
      <div className="min-h-[220px]">
        {creators.length === 0 ? (
          <div className="text-center py-12 text-textMuted">
            <p className="text-lg mb-2">No creators yet</p>
            <p className="text-sm">
              {isEditable 
                ? 'Add your first recommended creator to get started' 
                : 'No creators have been added for this week'}
            </p>
          </div>
        ) : (
          <div ref={rcListHostRef}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 md:gap-6" style={{ gap: 'clamp(1rem, 2vw, 1.5rem)' }}>
              {gridItems}
            </div>
          </div>
        )}
      </div>
        <CreatorModal 
          creator={selectedCreatorId ? creators.find(c => c.id === selectedCreatorId) || null : null} 
          open={modalOpen && !isEditable} 
          onClose={() => setModalOpen(false)} 
          role={cardRole}
        />
        <AnimatePresence>
          {showCropModal && (
            <ImageCropModal
              open={showCropModal}
              creatorId={selectedCreatorId}
              image={imageToCrop}
              onClose={() => setShowCropModal(false)}
              onCropComplete={handleCropComplete}
            />
          )}
        </AnimatePresence>
    </SectionContainer>
  );
});

RecommendedCreators.displayName = 'RecommendedCreators';

export default RecommendedCreators; 